<template>

  <!-- login page start-->
  <div class="container-fluid p-0">
    <div class="row m-0">
      <div class="col-12 p-0">
        <div class="login-card login-dark">
          <div>
            <div>
              <a class="logo" href="#">
                <img
                    class="img-fluid for-light m-auto"
                    src="assets/images/logo.svg"
                    alt="loginpage"
                />
                <img
                    class="img-fluid for-dark"
                    src="assets/images/logo.svg"
                    alt="logo"
                />
              </a>
            </div>
            <div class="login-main">
              <form class="theme-form" @submit.prevent="handleLogin">
                <h2 class="text-center">Sign In</h2>
                <p class="text-center">Enter your email &amp; password to login</p>

                <!-- Account locked banner — persists until page reload or admin unlock -->
                <div v-if="isLocked" class="alert alert-danger d-flex align-items-start gap-2 py-3" role="alert">
                  <span style="font-size:1.1rem;">🔒</span>
                  <div>
                    <strong>Account locked</strong><br/>
                    <span style="font-size:0.9rem;">{{ errorMessage }}</span>
                  </div>
                </div>

                <!-- Cooldown banner with live countdown -->
                <div v-else-if="cooldownSecondsRemaining > 0" class="alert alert-warning d-flex align-items-start gap-2 py-3" role="alert">
                  <span style="font-size:1.1rem;">⏱</span>
                  <div>
                    <strong>Too many failed attempts</strong><br/>
                    <span style="font-size:0.9rem;">
                      Please wait <strong>{{ formatCooldown(cooldownSecondsRemaining) }}</strong> before trying again.
                    </span>
                  </div>
                </div>

                <!-- Generic wrong-password error -->
                <p v-else-if="errorMessage" class="text-danger">{{ errorMessage }}</p>

                <div class="form-group">
                  <label class="col-form-label">Email Address</label>
                  <input
                      class="form-control"
                      type="email"
                      v-model="username"
                      required
                      placeholder="email@address.com"
                      @input="clearGenericError"
                  />
                </div>

                <div class="form-group">
                  <label class="col-form-label">Password</label>
                  <div class="form-input position-relative">
                    <input
                        class="form-control"
                        :type="showPassword ? 'text' : 'password'"
                        v-model="password"
                        required
                        placeholder="*********"
                        autocomplete="current-password"
                        @input="clearGenericError"
                    />
                    <div class="show-hide" @click="toggleShowPassword">
                      <span class="show"></span>
                    </div>
                  </div>
                </div>

                <div class="form-group mb-0 checkbox-checked">
                  <div class="text-end mt-3">
                    <button
                        class="btn btn-primary btn-block w-100"
                        type="submit"
                        :disabled="loading || isLocked || cooldownSecondsRemaining > 0"
                    >
                      <span v-if="isLocked">Account Locked</span>
                      <span v-else-if="cooldownSecondsRemaining > 0">
                        Try again in {{ formatCooldown(cooldownSecondsRemaining) }}
                      </span>
                      <span v-else>Sign in</span>
                    </button>
                  </div>
                </div>

              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {ref, onUnmounted} from 'vue';

// Reactive state
const username = ref('');
const password = ref('');
const errorMessage = ref(null);
const loading = ref(false);
const showPassword = ref(false);
const isLocked = ref(false);
const cooldownSecondsRemaining = ref(0);

let cooldownInterval = null;

// Clears only the transient wrong-password error when the user edits their credentials.
// Cooldown and locked states are server-driven and must not be cleared by typing.
const clearGenericError = () => {
  if (!isLocked.value && cooldownSecondsRemaining.value === 0) {
    errorMessage.value = null;
  }
};

const toggleShowPassword = () => {
  showPassword.value = !showPassword.value;
};

const formatCooldown = (seconds) => {
  if (seconds <= 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
};

const startCooldownCountdown = (seconds) => {
  clearInterval(cooldownInterval);
  cooldownSecondsRemaining.value = seconds;
  cooldownInterval = setInterval(() => {
    if (cooldownSecondsRemaining.value <= 1) {
      cooldownSecondsRemaining.value = 0;
      clearInterval(cooldownInterval);
      cooldownInterval = null;
    } else {
      cooldownSecondsRemaining.value--;
    }
  }, 1000);
};

const handleLogin = async () => {
  if (loading.value || isLocked.value || cooldownSecondsRemaining.value > 0) return;

  loading.value = true;
  errorMessage.value = null;

  try {
    const response = await fetch('/api/authenticate/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: username.value,
        password: password.value,
      }),
    });

    if (response.redirected) {
      // Successful login — follow the server redirect
      window.location.href = response.url;
    } else if (response.status === 403) {
      // Account is hard-locked; requires admin to unlock
      isLocked.value = true;
      const text = await response.text();
      errorMessage.value = text || 'Your account has been locked. Please contact support to regain access.';
    } else if (response.status === 429) {
      // Cooldown window active — read Retry-After header for precise duration
      const retryAfter = parseInt(response.headers.get('Retry-After'), 10);
      if (retryAfter > 0) {
        startCooldownCountdown(retryAfter);
      } else {
        // Fallback if header is missing
        startCooldownCountdown(30);
      }
      // Consume response body (not shown — countdown handles the UX)
      await response.text();
    } else {
      const errorText = await response.text();
      errorMessage.value = errorText || 'Invalid username or password.';
    }
  } catch (error) {
    errorMessage.value = 'An error occurred. Please try again later.';
  } finally {
    loading.value = false;
  }
};

// Clean up the interval if the component is ever torn down
onUnmounted(() => {
  clearInterval(cooldownInterval);
});
</script>
